import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Spade,
  Crown, Users,
  UserCircle, ChevronRight, ChevronDown, ShieldCheck, Smartphone,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { t, trServer, useLang } from "../lib/i18n";
import { LanguageSwitch } from "../components/LanguageSwitch";
import { BrandBackground } from "../components/BrandBackground";
import { EMBLEM_URL, LOGO_URL } from "../lib/brand";
import { Button } from "../components/ui/button";
import { toast } from "../lib/notify";
import { startGoogleLogin } from "../lib/googleLogin";
import { nameError } from "../lib/playerName";
import { createGuest, resumeGuest, useKnownGuest } from "../lib/guestDevice";
import { KnownGuestButton, NewGuestToggle } from "../components/KnownGuest";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function Landing() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  useLang();

  // The guest button (its name field opens right under it, and enters the app) + Google.
  const redirectTarget = new URLSearchParams(window.location.search).get("redirect");
  // Coming from an invite link: the name field is open from the start (a
  // logged-in player is sent on by the redirect effect anyway).
  const [guestOpen, setGuestOpen] = useState(() => !!redirectTarget);

  const [guestName, setGuestName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // The guest this device keeps (lib/guestDevice.js): one tap brings it back, unless the player asks for
  // a new profile.
  const [known, setKnown, knownReady] = useKnownGuest();
  const [newProfile, setNewProfile] = useState(false);
  const showKnown = !!known && !newProfile;

  const nameInputRef = useRef(null);
  // Where a guest goes once their profile exists. The redirect effect below
  // must use it: React Router applies navigations as transitions, so the user
  // update renders this page first and the effect would otherwise send them
  // to the lobby instead of where they were going.
  const afterLoginRef = useRef(null);

  useEffect(() => {
    if (!loading && user) {
      navigate(afterLoginRef.current || redirectTarget || "/lobby", { replace: true });
    }
  }, [user, loading, navigate, redirectTarget]);

  useEffect(() => {
    if (guestOpen) {
      setTimeout(() => nameInputRef.current?.focus(), 180);
    }
  }, [guestOpen]);

  // Google: the website goes to Google and back to /auth/callback; the apps use an in-app tab and come
  // back into the app once signed in (lib/googleLogin.js).
  const handleGoogleLogin = () => {
    afterLoginRef.current = redirectTarget || "/lobby";
    startGoogleLogin(redirectTarget).catch(() => toast.error(t("common.googleFailed")));
  };

  // A name is enough: the guest profile is made at once and the player enters the app (owner's request: the
  // draw pile's side, a second step until October 2026, is now in the Lobby's Table tab, on the right by default).
  const handleGuestSubmit = async () => {
    const name = guestName.trim();
    if (!name) { setError(t("common.enterName")); return; }
    // The server's rule for a name, said here first (lib/playerName.js).
    const bad = nameError(guestName);
    if (bad) { setError(bad); return; }
    setSubmitting(true);
    setError("");
    try {
      const guest = await createGuest(name);
      const target = redirectTarget || "/lobby";
      afterLoginRef.current = target;
      setUser(guest);
      navigate(target, { replace: true });
    } catch (err) {
      setError(trServer(err?.response?.data?.detail) || t("common.networkRetry"));
      setSubmitting(false);
    }
  };

  const handleResume = async () => {
    setSubmitting(true);
    setError("");
    try {
      const guest = await resumeGuest();
      const target = redirectTarget || "/lobby";
      afterLoginRef.current = target;
      setUser(guest);
      navigate(target, { replace: true });
    } catch (err) {
      // That account is gone: the name field takes its place.
      if (err.gone) { setKnown(null); setGuestOpen(true); setError(t("landing.guestGone")); }
      else setError(trServer(err?.response?.data?.detail) || t("common.networkRetry"));
      setSubmitting(false);
    }
  };

  const toggleNewProfile = () => {
    setNewProfile((o) => !o);
    setGuestOpen(true);
    setError("");
  };

  // The emblem steps back once the name field is on show.
  const compact = guestOpen && !showKnown;

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex flex-col">

      <BrandBackground />

      {/* ── Top bar ─────────────────────────────────────────────── */}
      {/* LTR in every language: the language switch keeps its place (owner's rule). */}
      <header dir="ltr" className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-3 sm:py-5">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt={t("common.brand")} className="h-9 w-9 rounded-lg ring-1 ring-amber-400/30" />
          <span className="font-display text-lg text-amber-300 italic tracking-wide">{t("common.brand")}</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitch />
          {user && (
            <Button
              onClick={() => navigate("/lobby")}
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-full text-sm px-4"
            >
              <Crown className="h-4 w-4 me-1.5" /> {t("common.lobby")}
            </Button>
          )}
        </div>
      </header>

      {/* ── Main centered area ──────────────────────────────────── */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-start px-6 pt-2 pb-6 sm:pt-8">

        {/* Brand title — always visible */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className="text-center mb-4 sm:mb-6"
        >
          {/* Emblem — steps back once the guest form takes the stage */}
          <motion.img
            src={EMBLEM_URL}
            alt=""
            draggable={false}
            initial={{ opacity: 0, scale: 0.8, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            width={760}
            height={657}
            className={`mx-auto mb-2 sm:mb-3 w-auto transition-[height] duration-300 ${
              compact ? "h-20 sm:h-24" : "h-40 sm:h-48"
            }`}
          />

          {/* Thin gold line accent */}
          <div className="flex items-center justify-center gap-3 mb-3 sm:mb-4">
            <div className="h-px w-8 sm:w-12 bg-gradient-to-r from-transparent to-amber-500/40" />
            <div className="h-1 w-1 rounded-full bg-amber-500/60" />
            <div className="h-px w-8 sm:w-12 bg-gradient-to-l from-transparent to-amber-500/40" />
          </div>

          <h1 className="font-display leading-tight">
            <span className="block text-3xl sm:text-4xl text-white tracking-tight">
              {t("landing.titlePre")}<span className="italic text-amber-400 drop-shadow-[0_0_24px_rgba(251,191,36,0.35)]">{t("landing.titleMain")}</span>
            </span>
            <span className="block text-sm sm:text-base text-slate-400/80 mt-1 tracking-wide font-light">
              {t("landing.subtitle")}
            </span>
          </h1>
        </motion.div>

        {/* ── Animated content panel ──────────────────────────── */}
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">

            {/* ── HERO — Google, or a guest (name field opens below) ── */}
            {(
              <motion.div
                key="hero"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14, scale: 0.97 }}
                transition={{ duration: 0.28 }}
                className="space-y-3"
              >
                {/* Google first (owner's request): the account keeps the player's data. On the website and in the
                    apps (owner's request, October 2026: the website had guests only for a while). */}
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  data-testid="google-btn"
                  className="group w-full flex items-center gap-3.5 bg-white/[0.06] hover:bg-white/[0.1] border border-white/15 hover:border-white/30 rounded-2xl px-4 py-3.5 text-start transition-all duration-200 cursor-pointer disabled:opacity-50"
                >
                  <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm">
                    <GoogleIcon />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[15px]">{t("common.google")}</div>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-emerald-300/90">
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                      <span>{t("landing.googleKeep")}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-slate-200 transition-colors shrink-0 rtl:rotate-180" />
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-white/[0.06]" />
                  <span className="text-slate-600 text-[11px] uppercase tracking-widest">{t("common.or")}</span>
                  <div className="flex-1 h-px bg-white/[0.06]" />
                </div>

                {/* Guest, second: the device's guest comes back in one tap, or the name field unfolds inside
                    the same card */}
                {knownReady && (<>
                <div className="w-full rounded-2xl p-px bg-gradient-to-br from-amber-400/60 via-amber-500/40 to-amber-600/20 shadow-lg shadow-amber-500/10">
                  <div className="rounded-2xl bg-slate-900/90">
                    {showKnown ? (
                      <>
                        <KnownGuestButton guest={known} onClick={handleResume} busy={submitting} />
                        {error && <p className="px-4 pb-3 -mt-1 text-red-400 text-sm">{error}</p>}
                      </>
                    ) : (
                    <button
                      data-testid="guest-btn"
                      onClick={() => { setGuestOpen((o) => !o); setError(""); }}
                      aria-expanded={guestOpen}
                      className="group w-full flex items-center gap-4 px-4 py-4 cursor-pointer"
                    >
                      <div className="h-11 w-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                        <UserCircle className="h-6 w-6 text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <div className="font-bold text-white text-[15px]">
                          {t("landing.guestTitle")}
                        </div>
                        {/* What becomes of a guest's data (owner's request): it is kept on this device. */}
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-amber-300/70">
                          <Smartphone className="h-3.5 w-3.5 shrink-0" />
                          <span>{t("landing.guestKept")}</span>
                        </div>
                      </div>
                      {guestOpen
                        ? <ChevronDown className="h-5 w-5 text-amber-300 shrink-0" />
                        : <ChevronRight className="h-5 w-5 text-amber-400/70 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-all shrink-0 rtl:rotate-180" />}
                    </button>
                    )}

                    <AnimatePresence initial={false}>
                      {guestOpen && !showKnown && (
                        <motion.div
                          key="name"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4">
                            {/* Name field — button embedded at the end */}
                            <div className="relative">
                              <input
                                ref={nameInputRef}
                                data-testid="guest-name-input"
                                type="text"
                                placeholder={t("common.namePlaceholder")}
                                maxLength={20}
                                value={guestName}
                                onChange={(e) => { setGuestName(e.target.value); setError(""); }}
                                onKeyDown={(e) => { if (e.key === "Enter" && !submitting) handleGuestSubmit(); }}
                                className="w-full bg-slate-800/50 border border-white/10 focus:border-amber-500/50 rounded-xl px-4 py-2.5 sm:py-3 text-white placeholder-slate-600 text-base transition-colors outline-none pe-[120px]"
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                data-testid="guest-continue-btn"
                                onClick={handleGuestSubmit}
                                disabled={!guestName.trim() || submitting}
                                className="absolute end-1.5 top-1/2 -translate-y-1/2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-semibold text-sm rounded-lg px-3 py-1.5 transition-all whitespace-nowrap flex items-center gap-1.5"
                              >
                                {submitting
                                  ? <span className="h-3.5 w-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                                  : redirectTarget ? t("common.joinArrow") : t("landing.continueArrow")}
                              </button>
                            </div>
                            <AnimatePresence>
                              {error && (
                                <motion.p
                                  key="err"
                                  initial={{ opacity: 0, y: -4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0 }}
                                  className="text-red-400 text-sm mt-2"
                                >
                                  {error}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                {known && <NewGuestToggle guest={known} open={newProfile} onToggle={toggleNewProfile} />}
                </>)}

              </motion.div>
            )}

          </AnimatePresence>

        </div>
      </main>

      {/* ── Bottom feature strip ─────────────────────────────── */}
      <footer className="relative z-10 pb-5 sm:pb-8 px-6">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
          {[
            { Icon: Users,    label: t("landing.feat.players") },
            { Icon: Crown,    label: t("landing.feat.bots") },
            { Icon: Spade,    label: t("landing.feat.realtime") },
          ].map(({ Icon, label }) => (
            <span key={label} className="flex items-center gap-1.5 text-slate-500 text-[11px] tracking-wide">
              <Icon className="h-3 w-3 text-amber-500/60" />
              {label}
            </span>
          ))}
        </div>
      </footer>

    </div>
  );
}
